import { Observable, from, of, timer } from 'rxjs';
import { filter, delayWhen, switchMap, map, share } from 'rxjs/operators';
import request, { IServerResponse } from '@utils/axiosWrapper/axiosWrapper';
import * as Ids from '@utils/ids';
import { CUSTOM_METRICS, reportCustomMetrics } from '@utils/owlReporter';
import { ifPoiEnableAutoAcceptLocally } from '@utils/poiHelper';
import { OrderDetailInfo, OrderBasicInfo } from '@business/pages/OrderManage/wmUnifiedOrder/types';
import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';
import { canBeHandledByCurrentPoi, getWmPoiId } from '../../pushSource/helpers';
import { OrderBusinessType, OrderType } from '../../shared/types';

/** 餐饮新形态灰度 */
const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);

const customMetricsReport = {
    success: ({
        isAutoAccept,
        businessType,
    }: {
        isAutoAccept: boolean;
        businessType: OrderBusinessType.WM | OrderBusinessType.PHF;
    }) => {
        reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: businessType,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: 0,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: isAutoAccept ? 1 : 0,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'message_hub',
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
        });
    },
    nonZeroBusinessCode: (code: number, { isAutoAccept }: { isAutoAccept: boolean }) => {
        reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: code,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: isAutoAccept ? 1 : 0,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'message_hub',
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
        });
    },
    fail: ({ isAutoAccept }: { isAutoAccept: boolean }) => {
        reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 0,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: isAutoAccept ? 1 : 0,
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'message_hub',
            [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
        });
    },
};

/**
 * 新订单 push 中用于区分订单类型的标识
 */
export enum PushOrderUserRole {
    /** 普通订单或拼好饭单买订单 */
    NORMAL = '-1',
    /** PHF拼单一起送主单 */
    PHF_MAIN = '1',
    /** PHF拼单一起送子单 */
    PHF_SUB = '2',
}

export type NewOrderPushData<U extends PushOrderUserRole = PushOrderUserRole> = {
    /** 订单视图ID */
    wmOrderViewId: string;
    /** 订单类型 */
    user_role: U;
    /** 是否是预订单 */
    isPreOrder: boolean;
};

/** 普通订单详情 */
export type WmOrderDetail = {
    businessType: OrderBusinessType.WM;
    orderInfo: OrderBasicInfo & { orderType: OrderType.NONE };
};

/** 拼好饭订单详情 */
export type PhfOrderDetail = {
    businessType: OrderBusinessType.PHF;
    orderInfo: {
        wm_poi_id: number;
        wm_order_id_view_str: string;
        orderType: OrderType.ORDINARY | OrderType.PHF_MAIN | OrderType.PHF_SUB;
    };
};

/** 旧的订单详情接口，用于获取拼好饭拼单详情 */
const getOrderDetailFromPush = (
    orderViewId: string,
    orderPoiId: string,
): Promise<PhfOrderDetail | null> => {
    const isAutoAccept = ifPoiEnableAutoAcceptLocally(orderPoiId);

    return request
        .rawGet<
            IServerResponse<
                {
                    orderInfo: string;
                    commonInfo: string;
                    businessType: number;
                }[]
            >
        >('/gw/v2/order/receive/unprocessed/r/orderFromPush/v2/common', {
            params: {
                wmOrderViewId: orderViewId,
                wmPoiId: Ids.getWmPoiId(),
                getNewVo: 1,
                doEncrypt: 1,
                isAutoAccept: window.CommConstants.IS_CHAIN ? 0 : 1,
            },
        })
        .then(res => {
            if (res.code === 0) {
                if (!Array.isArray(res.data) || !res.data.length) {
                    // 注意：这里接口里的业务状态码为0，但我们认为拉不到订单详情是一种潜在的异常。
                    // 这里挂一个特殊的业务状态码，以便在Raptor上灵活切换规则。
                    customMetricsReport.nonZeroBusinessCode(998877, { isAutoAccept });
                    return null;
                }

                const { orderInfo, commonInfo, businessType } = res.data[0];
                customMetricsReport.success({
                    isAutoAccept,
                    businessType,
                });

                return {
                    orderInfo: {
                        ...JSON.parse(orderInfo),
                        ...JSON.parse(commonInfo),
                    },
                    businessType,
                };
            } else {
                customMetricsReport.nonZeroBusinessCode(res.code, { isAutoAccept });
                return null;
            }
        })
        .catch(() => {
            customMetricsReport.fail({ isAutoAccept });
            return null;
        });
};

/** 新的订单详情接口 */
const getOrderDetailFromPushNew = (
    orderViewId: string,
    orderPoiId: string,
    useRole?: string,
): Promise<WmOrderDetail | PhfOrderDetail | null> => {
    // FIXME：临时观察用，isAutoAccept = 0 的量应该极少
    const isAutoAccept = ifPoiEnableAutoAcceptLocally(orderPoiId);

    return request
        .rawPostAsFormSubmit<
            IServerResponse<{
                orderInfo: string;
                commonInfo: string;
                businessType: OrderBusinessType;
                serverTime: number;
            }>
        >(
            UNIFIED_ORDER_GRAY
                ? '/gw/api/unified/r/order/detail/info'
                : '/gw/api/order/mix/detail/common',
            {
                orderViewId,
                wmPoiId: orderPoiId,
                blockTags: 'orderInfo',
            },
        )
        .then(res => {
            if (res.code === 0) {
                const { data } = res;
                if (!data) {
                    customMetricsReport.nonZeroBusinessCode(998877, { isAutoAccept });
                    return null;
                }

                customMetricsReport.success({ isAutoAccept, businessType: data.businessType });

                if (data.businessType === OrderBusinessType.WM) {
                    // 外卖
                    const orderInfo: OrderDetailInfo = JSON.parse(data.orderInfo || '{}');
                    const innerOrderInfo = UNIFIED_ORDER_GRAY
                        ? orderInfo.unifiedOrderInfo
                        : orderInfo.orderInfo;
                    if (!innerOrderInfo) {
                        customMetricsReport.nonZeroBusinessCode(998877, { isAutoAccept });
                        return null;
                    }
                    return {
                        businessType: OrderBusinessType.WM as const,
                        orderInfo: {
                            ...innerOrderInfo,
                            orderType: OrderType.NONE as const,
                        },
                    };
                } else if (UNIFIED_ORDER_GRAY) {
                    // 拼好饭新形态
                    const orderInfo: OrderDetailInfo = JSON.parse(data.orderInfo || '{}');
                    const innerOrderInfo = orderInfo.unifiedOrderInfo;
                    return {
                        businessType: OrderBusinessType.PHF as const,
                        orderInfo: {
                            wm_poi_id: Number(orderPoiId),
                            wm_order_id_view_str: orderViewId,
                            orderType:
                                useRole === PushOrderUserRole.NORMAL
                                    ? OrderType.ORDINARY
                                    : innerOrderInfo?.orderType || OrderType.ORDINARY,
                        },
                    };
                } else {
                    return {
                        businessType: OrderBusinessType.PHF as const,
                        orderInfo: {
                            wm_poi_id: Number(orderPoiId),
                            wm_order_id_view_str: orderViewId,
                            orderType: OrderType.ORDINARY as const,
                        },
                    };
                }
            } else {
                customMetricsReport.nonZeroBusinessCode(res.code, { isAutoAccept });
                return null;
            }
        })
        .catch(() => {
            customMetricsReport.fail({ isAutoAccept });
            return null;
        });
};

/** （通过详情接口）增强的新订单数据 */
export type EnhancedNewOrderPushData =
    | {
          origin: NewOrderPushData;
          detail: WmOrderDetail | PhfOrderDetail | null;
      }
    | {
          origin: NewOrderPushData<PushOrderUserRole.PHF_MAIN>;
          detail: PhfOrderDetail | null;
      }
    | {
          origin: NewOrderPushData<PushOrderUserRole.PHF_SUB>;
          detail: null;
      };

/** 新订单 Push 到达后请求订单接口，将获取到的详情和原始 Push 消息组合 */
const newOrderPushPipe = (
    source$: Observable<NewOrderPushData>,
): Observable<EnhancedNewOrderPushData> => {
    return source$.pipe(
        filter(message => canBeHandledByCurrentPoi(message)),
        delayWhen(() => timer(window.CommConstants?.IS_CHAIN ? 1000 : 0)),
        switchMap(message => {
            // 若本机未开启自动接单，不请求详情
            if (!ifPoiEnableAutoAcceptLocally(getWmPoiId(message))) {
                return of({
                    origin: message,
                    detail: null,
                    code: 1,
                }) as Observable<
                    | { origin: NewOrderPushData<PushOrderUserRole.NORMAL>; detail: null }
                    | { origin: NewOrderPushData<PushOrderUserRole.PHF_MAIN>; detail: null }
                    | { origin: NewOrderPushData<PushOrderUserRole.PHF_SUB>; detail: null }
                >;
            }

            if (message.user_role === PushOrderUserRole.NORMAL) {
                return from(
                    getOrderDetailFromPushNew(
                        message.wmOrderViewId,
                        getWmPoiId(message),
                        message.user_role,
                    ),
                ).pipe(
                    map(detail => {
                        return {
                            origin: message as NewOrderPushData<PushOrderUserRole.NORMAL>,
                            detail,
                            // FIXME: 修正此处的手动分配 code
                            code: 1,
                        };
                    }),
                );
            }

            if (message.user_role === PushOrderUserRole.PHF_MAIN) {
                // 命中餐饮新形态灰度，拼好饭主单详情接口用统一接口
                return from(
                    UNIFIED_ORDER_GRAY
                        ? getOrderDetailFromPushNew(message.wmOrderViewId, getWmPoiId(message))
                        : getOrderDetailFromPush(message.wmOrderViewId, getWmPoiId(message)),
                ).pipe(
                    map(detail => {
                        return {
                            origin: message as NewOrderPushData<PushOrderUserRole.PHF_MAIN>,
                            detail,
                            code: 1,
                        };
                    }),
                );
            }

            return of({
                origin: message as NewOrderPushData<PushOrderUserRole.PHF_SUB>,
                detail: null,
                code: 1,
            });
        }),
        share(),
    );
};

export default newOrderPushPipe;
export { OrderBusinessType };
